import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Zap, Plus, Edit, Trash2, ArrowLeft, CheckCircle, Settings, Play, Pause, Info
} from 'lucide-react';
import { categories } from '../mock';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const AutoApprovalRules = () => {
  const { countryConfig } = useCountry();
  const currencySymbol = countryConfig?.currency_symbol || 'Rs';
  const [rules, setRules] = useState([]);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    conditions: {
      verified_seller: false,
      min_seller_rating: '',
      category: '',
      min_price: '',
      max_price: ''
    },
    enabled: true,
    priority: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/ads/auto-approval/rules`,
        { headers: getAuthHeader() }
      );
      setRules(response.data.rules || []);
      setGlobalEnabled(response.data.global_enabled || false);
    } catch (err) {
      console.error('Error fetching rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGlobal = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/ads/auto-approval/settings`,
        { enabled: !globalEnabled },
        { headers: getAuthHeader() }
      );
      setGlobalEnabled(!globalEnabled);
    } catch (err) {
      alert('Failed to update settings');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Rule name is required');
      return;
    }

    // Build conditions object
    const conditions = {};
    if (formData.conditions.verified_seller) {
      conditions.verified_seller = true;
    }
    if (formData.conditions.min_seller_rating) {
      conditions.min_seller_rating = parseFloat(formData.conditions.min_seller_rating);
    }
    if (formData.conditions.category) {
      conditions.category = formData.conditions.category;
    }
    if (formData.conditions.min_price) {
      conditions.min_price = parseFloat(formData.conditions.min_price);
    }
    if (formData.conditions.max_price) {
      conditions.max_price = parseFloat(formData.conditions.max_price);
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        conditions,
        enabled: formData.enabled,
        priority: parseInt(formData.priority) || 0
      };

      if (editingRule) {
        await axios.put(
          `${API_URL}/api/admin/ads/auto-approval/rules/${editingRule.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Rule updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/ads/auto-approval/rules`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Rule created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/ads/auto-approval/rules/${ruleId}`,
        { headers: getAuthHeader() }
      );
      alert('Rule deleted successfully');
      fetchRules();
    } catch (err) {
      alert('Failed to delete rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      conditions: {
        verified_seller: rule.conditions?.verified_seller || false,
        min_seller_rating: rule.conditions?.min_seller_rating?.toString() || '',
        category: rule.conditions?.category || '',
        min_price: rule.conditions?.min_price?.toString() || '',
        max_price: rule.conditions?.max_price?.toString() || ''
      },
      enabled: rule.enabled,
      priority: rule.priority || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      conditions: {
        verified_seller: false,
        min_seller_rating: '',
        category: '',
        min_price: '',
        max_price: ''
      },
      enabled: true,
      priority: 0
    });
  };

  const getConditionsList = (conditions) => {
    const list = [];
    if (conditions.verified_seller) list.push('Verified Seller');
    if (conditions.min_seller_rating) list.push(`Rating ≥ ${conditions.min_seller_rating}`);
    if (conditions.category) list.push(`Category: ${conditions.category}`);
    if (conditions.min_price) list.push(`Price ≥ Rs ${conditions.min_price.toLocaleString()}`);
    if (conditions.max_price) list.push(`Price ≤ Rs ${conditions.max_price.toLocaleString()}`);
    return list;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/admin/ads">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Zap className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Auto-Approval Rules</h1>
                <p className="text-amber-100 text-sm">Automate ad approval based on conditions</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Global Toggle */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${globalEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Zap className={`h-6 w-6 ${globalEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">Auto-Approval System</h3>
                  <p className="text-sm text-gray-500">
                    {globalEnabled
                      ? 'Ads matching rules will be automatically approved'
                      : 'All ads require manual review'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{globalEnabled ? 'Enabled' : 'Disabled'}</span>
                <Switch checked={globalEnabled} onCheckedChange={handleToggleGlobal} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How Auto-Approval Works</p>
                <p>When a new ad is posted, the system checks each rule in order of priority. If any rule's conditions match, the ad is automatically approved. If no rules match, the ad remains pending for manual review.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rules List */}
        <Card>
          <CardHeader>
            <CardTitle>Approval Rules ({rules.length})</CardTitle>
            <CardDescription>Rules are checked in order of priority (highest first)</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading rules...</p>
            ) : rules.length === 0 ? (
              <div className="text-center py-12">
                <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No auto-approval rules yet</p>
                <Button onClick={() => setShowModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Rule
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div
                    key={rule.id}
                    className={`border rounded-lg p-4 ${rule.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm text-gray-400">#{index + 1}</span>
                          <h3 className="font-semibold">{rule.name}</h3>
                          <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                            {rule.enabled ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline">Priority: {rule.priority}</Badge>
                        </div>
                        {rule.description && (
                          <p className="text-sm text-gray-500 mb-3">{rule.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {getConditionsList(rule.conditions).map((condition, idx) => (
                            <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              {condition}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(rule)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(rule.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Rule Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Auto-Approval Rule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rule Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Trusted Sellers"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Higher = checked first</p>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe when this rule should apply..."
                rows={2}
              />
            </div>

            <div className="border rounded-lg p-4">
              <Label className="text-base font-medium mb-4 block">Conditions</Label>
              <p className="text-sm text-gray-500 mb-4">Ads must meet ALL selected conditions to be auto-approved</p>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Verified Seller</p>
                    <p className="text-sm text-gray-500">Only approve ads from identity-verified sellers</p>
                  </div>
                  <Switch
                    checked={formData.conditions.verified_seller}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, verified_seller: checked }
                    })}
                  />
                </div>

                <div>
                  <Label>Minimum Seller Rating</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={formData.conditions.min_seller_rating}
                    onChange={(e) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, min_seller_rating: e.target.value }
                    })}
                    placeholder="e.g., 4.0"
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.conditions.category || 'any'}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, category: value === 'any' ? '' : value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Price ({currencySymbol})</Label>
                    <Input
                      type="number"
                      value={formData.conditions.min_price}
                      onChange={(e) => setFormData({
                        ...formData,
                        conditions: { ...formData.conditions, min_price: e.target.value }
                      })}
                      placeholder="e.g., 1000"
                    />
                  </div>
                  <div>
                    <Label>Maximum Price ({currencySymbol})</Label>
                    <Input
                      type="number"
                      value={formData.conditions.max_price}
                      onChange={(e) => setFormData({
                        ...formData,
                        conditions: { ...formData.conditions, max_price: e.target.value }
                      })}
                      placeholder="e.g., 100000"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Enabled</Label>
                <p className="text-sm text-gray-500">Rule will be active immediately</p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editingRule ? 'Update Rule' : 'Create Rule'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutoApprovalRules;
